/**
 * Outlet safety classification — display metadata only.
 *
 * The monitor-only conversion removed every outlet write affordance, so
 * this module no longer gates any control. It survives because outlet
 * tiles and the tile picker still color outlets by class (blocked =
 * life support). Pure functions only — no React, no fetch.
 *
 * Keep the map in lockstep with `apps/agent/src/apex/outlet-safety.ts`.
 *
 * Pattern fallbacks (applied only when the outlet name isn't in
 * `OUTLET_SAFETY`):
 *
 * - `^VO_*`  → `safe`     (Apex virtual outputs are pure software)
 * - `^DOS_*` → `blocked`  (dosing pumps — chemistry hardware)
 * - otherwise → `restricted`
 */
import type { OutletSafetyClass } from './types';

/**
 * Explicit per-outlet classification. Anything not listed here is
 * resolved by `classifyOutlet` via the pattern fallbacks.
 */
export const OUTLET_SAFETY: Record<string, OutletSafetyClass> = {
  // SAFE — virtual outputs / explicitly opted-in:
  DT_Fan_On: 'safe',

  // BLOCKED — life-support and chemistry hardware.
  HEATER: 'blocked',
  Sump_Pump: 'blocked',
  Chill_Pump: 'blocked',
  ATO_SOL: 'blocked',
  SKIMMER: 'blocked',
  DOS_ALK: 'blocked',
  DOS_Cal: 'blocked',
  DOS_Kalk: 'blocked',
};

/**
 * Resolve an outlet name to its safety classification.
 *
 * Lookup order:
 *   1. Explicit entry in `OUTLET_SAFETY` (exact match).
 *   2. `^VO_` (case-insensitive)  → `safe`.
 *   3. `^DOS_` (case-insensitive) → `blocked`.
 *   4. Anything else              → `restricted`.
 */
export function classifyOutlet(name: string): OutletSafetyClass {
  const explicit = OUTLET_SAFETY[name];
  if (explicit) return explicit;
  if (/^VO_/i.test(name)) return 'safe';
  if (/^DOS_/i.test(name)) return 'blocked';
  return 'restricted';
}
